import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { COMPANY, CITIES } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

export default function ZonesIntervention() {
  useSeo({
    title:
      "Zones d'intervention — Serrurier 22 & 35 (Bretagne) | Mon Serrurier Breton",
    description:
      "Toutes les communes desservies par notre serrurier breton : Mont-Dol, Dol-de-Bretagne, Saint-Malo, Dinard, Dinan, Cancale, Plancoët, Matignon, Saint-Cast, Pleurtuit, Ploubalay et plus de 22 villes des Côtes-d'Armor (22) et d'Ille-et-Vilaine (35).",
    path: "/zones-intervention",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Zones d'intervention", path: "/zones-intervention" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: CITIES.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `Serrurier ${c.name}`,
          url: `${SITE.url}/serrurier-${c.slug}`,
        })),
      },
    ],
  });
  useEffect(() => {}, []);

  const byDept = CITIES.reduce((acc, c) => {
    (acc[c.dept] = acc[c.dept] || []).push(c);
    return acc;
  }, {});

  const depts = [
    { key: "22", label: "Côtes-d'Armor (22)" },
    { key: "35", label: "Ille-et-Vilaine (35)" },
  ];

  return (
    <div data-testid="page-zones-intervention">
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
          <MapPin className="w-4 h-4" /> Zones d'intervention
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
          Votre serrurier en <span className="text-teal-600">Bretagne</span>.
        </h1>
        <p className="text-slate-600 max-w-2xl mt-5 leading-relaxed">
          Basés à Mont-Dol (35120), nous intervenons dans un rayon
          d'environ <strong>{COMPANY.radiusKm} km</strong> — secteurs{" "}
          <strong>Côtes-d'Armor (22)</strong> et{" "}
          <strong>Ille-et-Vilaine (35)</strong>. Cliquez sur votre commune pour
          découvrir nos services locaux.
        </p>
      </section>

      {depts.map((d) => (
        <section
          key={d.key}
          data-testid={`zones-dept-${d.key}`}
          className="max-w-7xl mx-auto px-4 md:px-8 pb-14"
        >
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-5">
            {d.label}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(byDept[d.key] || [])
              .slice()
              .sort((a, b) => a.distance - b.distance)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/serrurier-${c.slug}`}
                  data-testid={`zone-link-${c.slug}`}
                  className="group bg-white border border-slate-200 hover:border-teal-500 hover:shadow-lg transition-all rounded-2xl p-5 flex flex-col justify-between gap-4"
                >
                  <div>
                    <div className="font-display text-lg font-bold text-slate-900 group-hover:text-teal-700">
                      Serrurier {c.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.postal}
                      {c.distance > 0 && ` · ~${c.distance} km`}
                      {c.distance === 0 && " · Notre base"}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-teal-700 font-semibold">
                      Voir la fiche
                    </span>
                    <ArrowRight className="w-4 h-4 text-teal-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
          </div>
        </section>
      ))}

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold">
              Votre commune n'est pas listée ?
            </h2>
            <p className="text-slate-300 mt-2">
              Nous étendons régulièrement notre zone, appelez-nous : on trouve
              une solution.
            </p>
          </div>
          <a
            href={`tel:${COMPANY.phoneDigits}`}
            data-testid="zones-cta-call"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-4 shadow-xl text-lg"
          >
            <Phone className="w-5 h-5" /> {COMPANY.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
